import { memo, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { useTrackAgentPillClicked } from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { WorkspaceAgentCommentCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentCommentCard';
import { useOpenWorkspaceAgentCommentPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentCommentPill';
import {
  toWorkspaceAgentCommentPillFromGetComment,
  type WorkspaceAgentCommentPill,
  workspaceAgentCommentPillCanOpen,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentCommentPillFromGetComment';
import { isWorkspaceAgentCommentMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentCommentMoniker';
import { isCommentUnresolvableError } from '~/swr-hooks/discussions/isCommentUnresolvableError';
import { usePrivateComment } from '~/swr-hooks/discussions/usePrivateComment';

export type PrivateWorkspaceAgentCommentPillChipProps = {
  pill: WorkspaceAgentCommentPill;
};

/** One Bugsnag report per comment per tab: re-mentions and re-scrolls are not new failures. */
const reportedCommentIds = new Set<string>();

const reportPillCommentErrorOnce = (commentId: string, error: unknown) => {
  if (reportedCommentIds.has(commentId)) {
    return;
  }

  reportedCommentIds.add(commentId);
  reportErrorToBugsnag({ context: 'Workspace agent comment pill could not resolve its comment', error });
};

export const PrivateWorkspaceAgentCommentPillChip = memo(({ pill }: PrivateWorkspaceAgentCommentPillChipProps) => {
  const needsFetch = isWorkspaceAgentCommentMonikerPill(pill);
  const { inView, ref } = useInView({ skip: !needsFetch, triggerOnce: true });
  const { data, error, isLoading } = usePrivateComment(
    { commentId: pill.commentId },
    { enabled: needsFetch && inView },
  );
  const resolved = useMemo(
    () => (data ? toWorkspaceAgentCommentPillFromGetComment({ comment: data, pill }) : pill),
    [data, pill],
  );
  const canOpen = workspaceAgentCommentPillCanOpen(resolved);
  const { openCommentPill } = useOpenWorkspaceAgentCommentPill();
  const { trackAgentPillClicked } = useTrackAgentPillClicked();

  const handleOpenComment = useCallback(() => {
    if (!canOpen) {
      return;
    }

    trackAgentPillClicked({ object: 'comment', object_count: 1, object_name: resolved.authorName });
    openCommentPill(resolved);
  }, [canOpen, openCommentPill, resolved, trackAgentPillClicked]);

  useEffect(() => {
    if (error && !isCommentUnresolvableError(error)) {
      reportPillCommentErrorOnce(pill.commentId, error);
    }
  }, [error, pill.commentId]);

  let content = <WorkspaceAgentPendingPill label="Comment" />;

  if (!needsFetch || data) {
    content = (
      <WorkspaceAgentCommentCard
        authorInitials={resolved.authorInitials}
        authorName={resolved.authorName}
        avatarUrl={data?.account.avatar || undefined}
        body={resolved.body}
        colorSeed={resolved.commentId}
        createdAt={resolved.createdAt}
        data-testid="WORKSPACE_AGENT_MESSAGE_COMMENT_PILL"
        onOpen={canOpen ? handleOpenComment : undefined}
        staleVersion={resolved.staleVersion}
      />
    );
  } else if (error && !isLoading) {
    content = <WorkspaceAgentUnavailablePill kind="reference" referenceId={pill.commentId} />;
  }

  return (
    <div className="w-full" ref={ref}>
      {content}
    </div>
  );
});

PrivateWorkspaceAgentCommentPillChip.displayName = 'PrivateWorkspaceAgentCommentPillChip';
